// Replenishment, the lead's page: what needs filling now and who's on it, the min and max levels that keep pick faces
// and line-side bins stocked, and how production lines are fed from released production orders. The work itself is
// done on the warehouse app (/wms/replen); every trip is a bin move posted in Business Central.
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox, ago } from "../ui";
import { useLoad } from "../hooks";
import { wms, type ReplOverview, type ReplRule, type ReplTask, type RuleInput } from "../wms/wmsApi";
import { getLocation, setLocation } from "../wms/ui";
import { whyText } from "../wms/Replenish";

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const STATE: Record<ReplRule["state"], { label: string; cls: string }> = {
  empty: { label: "Empty", cls: "danger" }, low: { label: "At or below min", cls: "warn" }, ok: { label: "OK", cls: "ok" }, over: { label: "Over max", cls: "info" }, off: { label: "Off", cls: "" },
};
const REASON_CLS: Record<string, string> = { pick_waiting: "danger", below_min: "info", line_feed: "warn" };
const HORIZONS = [4, 8, 12, 24, 48, 72, 168];
const hoursText = (h: number) => (h % 24 === 0 && h >= 48 ? `${h / 24} days` : `${h} hours`);

export function ReplenishmentPage() {
  const [loc, setLoc] = useState(getLocation());
  const locations = useLoad(() => wms.locations());
  const [tick, setTick] = useState(0);
  const ov = useLoad(() => (loc ? wms.replenOverview(loc) : Promise.resolve(null as ReplOverview | null)), [loc, tick]);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { if (!loc && locations.data?.length) { setLoc(locations.data[0].code); setLocation(locations.data[0].code); } }, [locations.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => setTick((x) => x + 1);
  const d = ov.data;
  const urgent = d?.tasks.filter((t) => t.priority === 1).length ?? 0;
  const working = d?.tasks.filter((t) => t.status === "started").length ?? 0;
  const short = d?.tasks.filter((t) => t.reserve && t.reserve.short > 0).length ?? 0;

  return (
    <>
      <CommandBar status={note ?? (d ? "Checked against Business Central just now" : undefined)}>
        <select className="select" style={{ width: 200, height: 30 }} aria-label="Location" value={loc} onChange={(e) => { setLoc(e.target.value); setLocation(e.target.value); setNote(null); }}>
          {!loc && <option value="">Choose a location</option>}
          {locations.data?.map((l) => <option key={l.code} value={l.code}>{l.code} · {l.name}</option>)}
        </select>
        <button className="cmd" onClick={() => { setNote(null); reload(); }} disabled={!loc}>Check now</button>
        <Link className="cmd" to="/wms/replen">Open in the warehouse app</Link>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Replenishment</h1><span className="sub">Pick faces and production lines{loc ? ` at ${loc}` : ""}, from what's in the bins in Business Central</span></div>
        <ErrorBox error={locations.error ?? ov.error} />
        {!loc ? <div className="card"><div className="empty">Choose a location.</div></div> : !d ? <div className="card"><div className="empty">Checking bins at {loc}…</div></div> : (
          <>
            <div className="rp-kpis">
              <Kpi label="Bins to fill" value={d.tasks.length} />
              <Kpi label="Urgent" value={urgent} tone={urgent ? "danger" : undefined} hint="A pick needs more than the bin has, or a line's component is due within 4 hours" />
              <Kpi label="Being worked" value={working} />
              <Kpi label="Reserve can't cover" value={short} tone={short ? "warn" : undefined} hint="Not enough in reserve bins for the whole task" />
            </div>
            <TasksCard tasks={d.tasks} onChange={reload} setNote={setNote} />
            <RulesCard loc={loc} d={d} onChange={reload} setNote={setNote} />
            <div className="rp-cols">
              <FeedCard loc={loc} d={d} onChange={reload} setNote={setNote} />
              <RecentCard recent={d.recent} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Kpi({ label, value, tone, hint }: { label: string; value: number; tone?: "danger" | "warn"; hint?: string }) {
  return <div className="rp-kpi" title={hint}><span className="muted">{label}</span><b className={tone ? `rp-${tone}` : ""}>{value}</b></div>;
}

function TasksCard({ tasks, onChange, setNote }: { tasks: ReplTask[]; onChange: () => void; setNote: (s: string | null) => void }) {
  const release = async (t: ReplTask) => {
    try { await wms.replenRelease(t.id); setNote(`${t.toBin} is back on the list for anyone.`); onChange(); } catch (e) { setNote(errText(e)); }
  };
  return (
    <section className="card rp-card">
      <div className="card-head"><h2>To fill now</h2><span className="muted">Urgent first. The warehouse app shows the same list.</span></div>
      {!tasks.length ? <div className="empty">Nothing needs filling. Every bin with a rule is above its min, and lines have what production needs.</div> : (
        <div className="table-wrap"><table className="dgrid compact">
          <thead><tr><th>Why</th><th>Fill</th><th>Item</th><th>Detail</th><th className="r">To bring</th><th>From</th><th>Who</th><th /></tr></thead>
          <tbody>{tasks.map((t) => (
            <tr key={t.id}>
              <td><span className={`status ${REASON_CLS[t.reason] ?? ""}`}>{t.reasonLabel}</span></td>
              <td className="mono"><b>{t.toBin}</b></td>
              <td><b>{t.itemNo}</b> <span className="muted">{t.description}</span></td>
              <td className="muted">{whyText(t)}</td>
              <td className="r nowrap"><b>{fmt(t.remaining)}</b> {t.uom}{t.movedQty > 0 && <div className="muted sm">{fmt(t.movedQty)} of {fmt(t.qty)} moved</div>}</td>
              <td className="mono nowrap">{t.from ?? <span className="rp-warn">None in reserve</span>}{t.from && t.reserve && t.reserve.short > 0 && <div className="rp-warn sm">only {fmt(t.reserve.available)} in reserve</div>}</td>
              <td className="nowrap">{t.assignedTo ?? <span className="muted">–</span>}</td>
              <td className="r">{t.status === "started" && <button className="btn" onClick={() => release(t)} title="Put it back on the list for anyone; what's moved still counts">Release</button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </section>
  );
}

type Draft = { binCode: string; itemNo: string; min: string; max: string; multiple: string; kind: "pick_face" | "line_side"; sourceZone: string };
const blank: Draft = { binCode: "", itemNo: "", min: "", max: "", multiple: "", kind: "pick_face", sourceZone: "" };
const toInput = (f: Draft): RuleInput => ({ min: Number(f.min), max: Number(f.max), multiple: f.multiple.trim() ? Number(f.multiple) : null, kind: f.kind, sourceZone: f.sourceZone || null, active: true });

function RulesCard({ loc, d, onChange, setNote }: { loc: string; d: ReplOverview; onChange: () => void; setNote: (s: string | null) => void }) {
  const [f, setF] = useState<Draft>(blank);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<{ id: string; f: Draft } | null>(null);
  const [filter, setFilter] = useState("");
  const add = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await wms.addRule({ location: loc, binCode: f.binCode, itemNo: f.itemNo, ...toInput(f) });
      setNote(`Rule added for ${f.binCode.toUpperCase()}.`); setF({ ...blank, kind: f.kind, sourceZone: f.sourceZone }); onChange();
    } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };
  const save = async () => {
    if (!edit) return;
    setErr(null);
    try { await wms.updateRule(edit.id, toInput(edit.f)); setEdit(null); onChange(); } catch (x) { setErr(errText(x)); }
  };
  const toggle = async (r: ReplRule) => { try { await wms.updateRule(r.id, { active: !r.active }); onChange(); } catch (x) { setErr(errText(x)); } };
  const remove = async (r: ReplRule) => {
    if (!confirm(`Delete the rule for ${r.itemNo} in ${r.binCode}? Its open task goes too.`)) return;
    try { await wms.deleteRule(r.id); setNote(`Rule for ${r.binCode} deleted.`); onChange(); } catch (x) { setErr(errText(x)); }
  };
  const zoneSelect = (value: string, on: (v: string) => void, id: string, cell = false) => (
    <select id={id} className={`select${cell ? " cell" : ""}`} value={value} onChange={(e) => on(e.target.value)} aria-label="Take from zone first">
      <option value="">Any reserve</option>{d.zones.map((z) => <option key={z} value={z}>{z}</option>)}
    </select>
  );
  const rows = d.rules.filter((r) => !filter || `${r.binCode} ${r.itemNo} ${r.description}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <section className="card rp-card">
      <div className="card-head"><h2>Min and max levels</h2><span className="muted">When a bin drops to its min, top it up to its max. Open picks from the bin count against it.</span></div>
      <form className="rp-add" onSubmit={add}>
        <div className="field"><label htmlFor="rb">Bin</label><input id="rb" className="input mono" value={f.binCode} onChange={(e) => setF({ ...f, binCode: e.target.value })} placeholder="P-01" required /></div>
        <div className="field"><label htmlFor="ri">Item</label><input id="ri" className="input mono" value={f.itemNo} onChange={(e) => setF({ ...f, itemNo: e.target.value })} placeholder="Item no. or barcode" required /></div>
        <div className="field"><label htmlFor="rmin">Min</label><input id="rmin" className="input r" inputMode="decimal" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} required /></div>
        <div className="field"><label htmlFor="rmax">Max</label><input id="rmax" className="input r" inputMode="decimal" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} required /></div>
        <div className="field"><label htmlFor="rmul">Round up to</label><input id="rmul" className="input r" inputMode="decimal" value={f.multiple} onChange={(e) => setF({ ...f, multiple: e.target.value })} placeholder="Case qty" /></div>
        <div className="field"><label htmlFor="rk">Bin is a</label><select id="rk" className="select" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Draft["kind"] })}><option value="pick_face">Pick face</option><option value="line_side">Line-side kanban</option></select></div>
        <div className="field"><label htmlFor="rz">Take from</label>{zoneSelect(f.sourceZone, (v) => setF({ ...f, sourceZone: v }), "rz")}</div>
        <div className="field"><span className="label" aria-hidden="true">&nbsp;</span><button className="btn primary" disabled={busy}>{busy ? "Adding…" : "Add rule"}</button></div>
      </form>
      {err && <div className="msg error" style={{ margin: "0 16px 12px" }}>{err}</div>}
      {d.rules.length > 8 && <div className="filters" style={{ paddingTop: 0 }}><input className="input" style={{ width: 260 }} placeholder="Filter by bin or item" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter rules" /></div>}
      {!d.rules.length ? <div className="empty">No levels yet. Add your pick faces and line-side bins above. Quantities are in the item's base unit.</div> : (
        <div className="table-wrap"><table className="dgrid compact">
          <thead><tr><th>Bin</th><th>Item</th><th>Kind</th><th className="r">Min</th><th className="r">Max</th><th className="r">Round up to</th><th>Take from</th><th className="r">In bin</th><th>State</th><th /></tr></thead>
          <tbody>{rows.map((r) => {
            const e = edit?.id === r.id ? edit.f : null;
            const set = (k: keyof Draft, v: string) => setEdit({ id: r.id, f: { ...edit!.f, [k]: v } });
            return (
              <tr key={r.id} className={r.active ? "" : "rp-off"}>
                <td className="mono"><b>{r.binCode}</b></td>
                <td><b>{r.itemNo}</b> <span className="muted">{r.description}</span></td>
                <td>{e ? <select className="select cell" value={e.kind} onChange={(x) => set("kind", x.target.value)} aria-label="Kind"><option value="pick_face">Pick face</option><option value="line_side">Line-side</option></select> : r.kind === "line_side" ? "Line-side" : "Pick face"}</td>
                <td className="r">{e ? <input className="input cell r rp-num" value={e.min} onChange={(x) => set("min", x.target.value)} aria-label="Min" /> : fmt(r.min)}</td>
                <td className="r">{e ? <input className="input cell r rp-num" value={e.max} onChange={(x) => set("max", x.target.value)} aria-label="Max" /> : fmt(r.max)}</td>
                <td className="r">{e ? <input className="input cell r rp-num" value={e.multiple} onChange={(x) => set("multiple", x.target.value)} aria-label="Round up to" /> : r.multiple ? fmt(r.multiple) : <span className="muted">–</span>}</td>
                <td>{e ? zoneSelect(e.sourceZone, (v) => set("sourceZone", v), `z-${r.id}`, true) : r.sourceZone ?? <span className="muted">Any</span>}</td>
                <td className="r nowrap"><b>{fmt(r.onHand)}</b> {r.uom}{r.pickDemand > 0 && <div className="muted sm">picks need {fmt(r.pickDemand)}</div>}</td>
                <td><span className={`status ${STATE[r.state].cls}`}>{STATE[r.state].label}</span>{r.task && <span className="muted sm"> · {r.task.status === "started" ? "being filled" : "on the list"}</span>}</td>
                <td className="r nowrap">
                  {e ? <><button className="btn primary" onClick={save}>Save</button> <button className="btn" onClick={() => setEdit(null)}>Cancel</button></> : <>
                    <button className="btn" onClick={() => setEdit({ id: r.id, f: { binCode: r.binCode, itemNo: r.itemNo, min: String(r.min), max: String(r.max), multiple: r.multiple ? String(r.multiple) : "", kind: r.kind, sourceZone: r.sourceZone ?? "" } })}>Edit</button>{" "}
                    <button className="btn" onClick={() => toggle(r)}>{r.active ? "Turn off" : "Turn on"}</button>{" "}
                    <button className="btn" onClick={() => remove(r)} aria-label={`Delete the rule for ${r.itemNo} in ${r.binCode}`}>Delete</button></>}
                </td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
    </section>
  );
}

function FeedCard({ loc, d, onChange, setNote }: { loc: string; d: ReplOverview; onChange: () => void; setNote: (s: string | null) => void }) {
  const [s, setS] = useState(d.settings);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setS(d.settings), [d.settings]);
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    try { await wms.saveReplSettings({ location: loc, ...s }); setNote("Saved. The list has been checked again."); onChange(); } catch (x) { setErr(errText(x)); }
  };
  const ex = new Set(s.excludeZones);
  return (
    <form className="card rp-card" onSubmit={save}>
      <div className="card-head"><h2>Production feed</h2><span className="muted">From released production orders</span></div>
      <div className="card-body">
        {!d.feed.available && <div className="msg warn">Business Central needs the Courage WMS extension 1.1 for production components. Min and max levels work without it.</div>}
        <label className="rp-check"><input type="checkbox" checked={s.feedEnabled} onChange={(e) => setS({ ...s, feedEnabled: e.target.checked })} /> Feed lines from released production orders</label>
        <p className="muted sm" style={{ margin: "4px 0 14px 24px" }}>Components due within the window are netted against what's already in their bin, the To-Production bin BC puts on them. A line-side kanban for the same bin and item runs instead.</p>
        <div className="sp-two">
          <div className="field"><label htmlFor="fh">Look ahead</label>
            <select id="fh" className="select" value={s.feedHorizonHours} onChange={(e) => setS({ ...s, feedHorizonHours: Number(e.target.value) })} disabled={!s.feedEnabled}>
              {[...new Set([...HORIZONS, s.feedHorizonHours])].sort((a, b) => a - b).map((h) => <option key={h} value={h}>{hoursText(h)}</option>)}
            </select><span className="hint">About one shift ahead suits most lines</span></div>
          <div className="field"><label htmlFor="fz">Take from zone first</label>
            <select id="fz" className="select" value={s.feedSourceZone ?? ""} onChange={(e) => setS({ ...s, feedSourceZone: e.target.value || null })} disabled={!s.feedEnabled}>
              <option value="">Any reserve</option>{d.zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select></div>
        </div>
        <fieldset className="rp-zones"><legend className="label">Never take stock from these zones</legend>
          {d.zones.length ? d.zones.map((z) => <label key={z}><input type="checkbox" checked={ex.has(z)} onChange={(e) => setS({ ...s, excludeZones: e.target.checked ? [...ex, z] : [...ex].filter((x) => x !== z) })} /> {z}</label>)
            : <span className="muted">No zones on the bins at {loc}.</span>}
          <span className="hint">For pick faces and lines alike: docks, QC, quarantine. Bins blocked for taking stock out are never used.</span>
        </fieldset>
        {err && <div className="msg error">{err}</div>}
        <button className="btn primary">Save</button>
        {d.feed.available && s.feedEnabled && (
          <div style={{ marginTop: 16 }}>
            <div className="label" style={{ marginBottom: 6 }}>Line-side bins with work in the window</div>
            {d.feed.bins.length ? <table className="dgrid compact"><tbody>{d.feed.bins.map((b) => (
              <tr key={b.bin}><td className="mono"><b>{b.bin}</b></td><td className="muted">{b.orders} production order{b.orders === 1 ? "" : "s"}</td><td className="muted">{b.items} component{b.items === 1 ? "" : "s"}</td></tr>
            ))}</tbody></table> : <div className="muted">No released components with a bin are due in the next {hoursText(d.settings.feedHorizonHours)}.</div>}
          </div>
        )}
      </div>
    </form>
  );
}

function RecentCard({ recent }: { recent: ReplTask[] }) {
  return (
    <section className="card rp-card">
      <div className="card-head"><h2>Recently filled</h2></div>
      {!recent.length ? <div className="empty">Nothing yet.</div> : (
        <table className="dgrid compact"><tbody>{recent.map((t) => (
          <tr key={t.id}>
            <td className="mono"><b>{t.toBin}</b></td>
            <td>{t.itemNo}</td>
            <td className="r nowrap">{fmt(t.movedQty)}{t.status === "short" ? ` of ${fmt(t.qty)}` : ""} {t.uom}</td>
            <td>{t.status === "short" ? <span className="status warn">Short</span> : <span className="status ok">Filled</span>}</td>
            <td className="muted nowrap">{t.assignedTo ?? ""}</td>
            <td className="muted nowrap">{t.doneAt ? ago(t.doneAt) : ""}</td>
          </tr>
        ))}</tbody></table>
      )}
    </section>
  );
}
